"use client";

import { useState, useEffect } from "react";
import moment from 'moment';
import 'moment/locale/fr';

// Configuration de moment.js pour le français
moment.locale('fr');

interface AppointmentCalendarProps {
  serviceType: 'installation' | 'reparation';
  selectedService: string;
  serviceOption: string;
  onDateSelect: (date: Date) => void;
  onTimeSelect: (time: string) => void;
  selectedDate: Date | null;
  selectedTime: string | null;
  onContinue: () => void;
  multipleServices?: Array<{
    type: 'installation' | 'reparation';
    service: string;
    option: string;
  }>;
  isMultipleServices?: boolean;
}

interface Appointment {
  id: string;
  date: Date;
  startTime: Date;
  endTime: Date;
  status: string;
  service?: {
    type: string;
  };
}

interface BlockedSlot {
  date: string;
  time: string;
}

// 🎯 RÈGLES MÉTIER CENTRALISÉES
const BUSINESS_RULES = {
  periods: {
    morning: { start: 8, end: 12, maxPoints: 2 },
    afternoon: { start: 13, end: 23, maxPoints: 3 }
  },
  servicePoints: {
    morning: { reparation: 1, installation: 2 },
    afternoon: { reparation: 1, installation: 1.5 }
  },
  // Durée estimée en heures par type (bloque les créneaux suivants)
  estimatedDuration: {
    reparation: 2,
    installation: 3,
  },
  maxAdvanceMonths: 6
};

// 🔧 TYPES POUR LA DISPONIBILITÉ
interface PeriodAvailability {
  pointsUsed: number;
  pointsMax: number;
  pointsAvailable: number;
  canAddService: boolean;
  availableSlots: string[];
}

interface DayAvailability {
  morning: PeriodAvailability;
  afternoon: PeriodAvailability;
  isFullyBooked: boolean;
  isCompletelyBlocked: boolean;
  hasAnyAvailability: boolean;
}

export default function AppointmentCalendar({
  serviceType,
  selectedService,
  serviceOption,
  onDateSelect,
  onTimeSelect,
  selectedDate,
  selectedTime,
  onContinue,
  multipleServices,
  isMultipleServices,
}: AppointmentCalendarProps) {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [blockedSlots, setBlockedSlots] = useState<BlockedSlot[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Navigation mensuelle
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // 📡 CHARGEMENT DES DONNÉES
  // Formatte une date locale en "YYYY-MM-DD" sans décalage UTC
  const toLocalDateStr = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const fetchData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      const timestamp = Date.now();
      const sixMonthsLater = new Date(timestamp + 180 * 24 * 60 * 60 * 1000);
      const toDate = toLocalDateStr(sixMonthsLater);

      // Charger les rendez-vous (6 mois, 500 max)
      const appointmentsResponse = await fetch(`/api/appointments/public?limit=500&to=${toDate}&_t=${timestamp}`);
      if (appointmentsResponse.ok) {
        const appointmentsData = await appointmentsResponse.json();
        setAppointments(appointmentsData);
      }

      // Charger les créneaux bloqués
      const blockedResponse = await fetch(`/api/blocked-slots?_t=${timestamp}`);
      if (blockedResponse.ok) {
        const blockedData = await blockedResponse.json();
        setBlockedSlots(blockedData.blockedSlots || []);
      }

    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Chargement initial + polling toutes les 30s (pas de refetch au changement de mois)
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(false);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // 🔧 FONCTIONS UTILITAIRES
  
  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isPastTimeSlot = (date: Date, timeString: string): boolean => {
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Si ce n'est pas aujourd'hui, utiliser la logique de date passée
    if (date.getTime() !== today.getTime()) {
      return isPastDate(date);
    }
    
    // Si c'est aujourd'hui, vérifier si l'heure est passée
    const [hours, minutes] = timeString.split(':').map(Number);
    const slotTime = new Date(date);
    slotTime.setHours(hours, minutes, 0, 0);
    
    return slotTime <= now;
  };


  const isSlotManuallyBlocked = (date: Date, timeString: string): boolean => {
    const dateString = toLocalDateStr(date);
    return blockedSlots.some(slot =>
      slot.date === dateString && slot.time === timeString
    );
  };

  const getAppointmentsForDate = (date: Date): Appointment[] => {
    return appointments.filter((apt) => {
      const aptDate = new Date(apt.date);
      return (
        aptDate.getDate() === date.getDate() &&
        aptDate.getMonth() === date.getMonth() &&
        aptDate.getFullYear() === date.getFullYear() &&
        apt.status !== 'CANCELLED'
      );
    });
  };

  const getAppointmentsForPeriod = (dayAppointments: Appointment[], period: 'morning' | 'afternoon'): Appointment[] => {
    const { start, end } = BUSINESS_RULES.periods[period];
    return dayAppointments.filter(apt => {
      const hour = new Date(apt.startTime).getHours();
      return hour >= start && hour < end;
    });
  };

  // 🎯 CALCUL DE LA DISPONIBILITÉ D'UNE PÉRIODE
  // Règles :
  //   1. Capacité par points (matin 2pts, après-midi 3pts)
  //   2. Chaque RDV bloque sa durée estimée (réparation 2h, installation 3h)
  //   3. Un nouveau créneau doit terminer dans la période
  const calculatePeriodAvailability = (
    date: Date,
    period: 'morning' | 'afternoon',
    dayAppointments: Appointment[]
  ): PeriodAvailability => {
    const { start, end, maxPoints } = BUSINESS_RULES.periods[period];
    const servicePoints = BUSINESS_RULES.servicePoints[period];
    const newDuration = BUSINESS_RULES.estimatedDuration[serviceType];

    // Weekend matin → fermé
    const dayOfWeek = date.getDay();
    if ((dayOfWeek === 0 || dayOfWeek === 6) && period === 'morning') {
      return { pointsUsed: maxPoints, pointsMax: maxPoints, pointsAvailable: 0, canAddService: false, availableSlots: [] };
    }

    const periodAppointments = getAppointmentsForPeriod(dayAppointments, period);

    // Calculer les points utilisés
    const pointsUsed = periodAppointments.reduce((total, apt) => {
      return total + (apt.service?.type === 'installation' ? servicePoints.installation : servicePoints.reparation);
    }, 0);

    const pointsAvailable = Math.max(0, maxPoints - pointsUsed);
    const requiredPoints = servicePoints[serviceType];
    const canAddService = pointsAvailable >= requiredPoints;

    // Générer les créneaux disponibles
    const availableSlots: string[] = [];
    for (let hour = start; hour < end; hour++) {
      // Le créneau doit permettre de terminer la prestation dans la période
      if (hour + newDuration > end) break;

      const timeString = `${hour.toString().padStart(2, '0')}:00`;

      // Vérifier que le créneau ne chevauche pas la durée d'un RDV existant
      const isOccupied = periodAppointments.some(apt => {
        const aptHour = new Date(apt.startTime).getHours();
        const aptDuration = BUSINESS_RULES.estimatedDuration[
          apt.service?.type === 'installation' ? 'installation' : 'reparation'
        ];
        return hour >= aptHour && hour < aptHour + aptDuration;
      });

      if (!isPastTimeSlot(date, timeString) && !isSlotManuallyBlocked(date, timeString) && !isOccupied && canAddService) {
        availableSlots.push(timeString);
      }
    }

    return {
      pointsUsed,
      pointsMax: maxPoints,
      pointsAvailable,
      canAddService,
      availableSlots
    };
  };

  // 🎯 CALCUL DE LA DISPONIBILITÉ D'UNE JOURNÉE
  const calculateDayAvailability = (date: Date): DayAvailability => {
    const dayAppointments = getAppointmentsForDate(date);
    
    const morning = calculatePeriodAvailability(date, 'morning', dayAppointments);
    const afternoon = calculatePeriodAvailability(date, 'afternoon', dayAppointments);

    // Vérifier si toute la journée est bloquée manuellement
    const allWorkingHours = [];
    for (let hour = 8; hour < 12; hour++) {
      allWorkingHours.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    for (let hour = 13; hour < 23; hour++) {
      allWorkingHours.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    
    const isCompletelyBlocked = allWorkingHours.every(time => 
      isSlotManuallyBlocked(date, time)
    );

    const isFullyBooked = !morning.canAddService && !afternoon.canAddService;
    const hasAnyAvailability = morning.availableSlots.length > 0 || afternoon.availableSlots.length > 0;

    return {
      morning,
      afternoon,
      isFullyBooked,
      isCompletelyBlocked,
      hasAnyAvailability: hasAnyAvailability && !isCompletelyBlocked
    };
  };

  // 🎯 NAVIGATION MENSUELLE
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    const currentDate = new Date();
    const maxFutureDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + BUSINESS_RULES.maxAdvanceMonths, 1);
    const nextMonth = new Date(currentYear, currentMonth + 1, 1);
    
    if (nextMonth <= maxFutureDate) {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const canGoPrevious = () => {
    const today = new Date();
    const currentDisplayMonth = new Date(currentYear, currentMonth);
    const todayMonth = new Date(today.getFullYear(), today.getMonth());
    return currentDisplayMonth > todayMonth;
  };

  const canGoNext = () => {
    const currentDate = new Date();
    const maxFutureDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + BUSINESS_RULES.maxAdvanceMonths, 1);
    const nextMonth = new Date(currentYear, currentMonth + 1, 1);
    return nextMonth <= maxFutureDate;
  };

  // 🎯 GESTION DES CLICS
  const handleDateClick = (date: Date) => {
    if (isPastDate(date)) return;
    
    const dayAvailability = calculateDayAvailability(date);
    if (!dayAvailability.hasAnyAvailability) {
      console.log(`Jour indisponible : ${date.toDateString()}`);
      return;
    }
    
    onDateSelect(date);
  };

  // 🎯 GÉNÉRATION DU CALENDRIER
  const renderCalendar = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    
    let startDayOfWeek = firstDayOfMonth.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;
    
    const daysFromPrevMonth = startDayOfWeek;
    const daysInMonth = lastDayOfMonth.getDate();
    const totalDaysNeeded = daysFromPrevMonth + daysInMonth;
    const weeksNeeded = Math.ceil(totalDaysNeeded / 7);
    const totalCells = weeksNeeded * 7;
    
    const calendarDays = [];
    
    // Jours du mois précédent
    const prevMonth = new Date(currentYear, currentMonth, 0);
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = new Date(prevMonth);
      day.setDate(prevMonth.getDate() - i);
      calendarDays.push({ date: day, isCurrentMonth: false });
    }
    
    // Jours du mois actuel
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const date = new Date(currentYear, currentMonth, day);
      calendarDays.push({ date, isCurrentMonth: true });
    }
    
    // Jours du mois suivant
    const remainingCells = totalCells - calendarDays.length;
    for (let day = 1; day <= remainingCells; day++) {
      const date = new Date(currentYear, currentMonth + 1, day);
      calendarDays.push({ date, isCurrentMonth: false });
    }
    
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-4">
          {/* En-tête du calendrier */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold">
                Sélectionnez une date
              </h3>
            </div>
            
            {/* Navigation mensuelle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={goToPreviousMonth}
                disabled={!canGoPrevious()}
                className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Mois précédent"
              >
                ←
              </button>
              
              <div className="font-medium text-lg text-center px-4">
                {new Date(currentYear, currentMonth).toLocaleDateString('fr-FR', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </div>
              
              <button
                onClick={goToNextMonth}
                disabled={!canGoNext()}
                className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Mois suivant"
              >
                →
              </button>
            </div>
          </div>
          
          <div className="calendar-container">
            <div className="grid grid-cols-7 gap-1 text-center">
              {["L", "M", "M", "J", "V", "S", "D"].map((day, index) => (
                <div key={index} className="py-2 font-medium text-gray-600">{day}</div>
              ))}
              
              {calendarDays.map((dayInfo, i) => {
                const { date, isCurrentMonth } = dayInfo;
                
                const isToday = date.getTime() === today.getTime();
                const isPast = isPastDate(date);
                const dayAvailability = isCurrentMonth ? calculateDayAvailability(date) : null;
                const isAvailable = isCurrentMonth && !isPast && dayAvailability?.hasAnyAvailability;
                const isSelected = selectedDate && 
                  date.getDate() === selectedDate.getDate() && 
                  date.getMonth() === selectedDate.getMonth() &&
                  date.getFullYear() === selectedDate.getFullYear();
                  
                return (
                  <div 
                    key={i}
                    onClick={() => isAvailable && handleDateClick(new Date(date))}
                    className={`py-3 text-sm border rounded-md transition-colors cursor-pointer
                      ${isSelected 
                        ? "bg-primary text-white" 
                        : isAvailable
                        ? "hover:bg-gray-100 text-gray-900"
                        : "bg-gray-50 text-gray-300 cursor-not-allowed"
                      }
                      ${isToday && !isSelected && isCurrentMonth ? "border-primary border-2 font-bold" : ""}
                      ${!isCurrentMonth ? "bg-gray-50" : ""}
                    `}
                  >
                    {date.getDate()}
                  </div>
                );
              })}
            </div>
            
            {/* Légende */}
            <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm text-gray-600">
              <div className="flex items-center">
                <div className="w-4 h-4 border-2 border-primary rounded mr-2"></div>
                <span>Aujourd'hui</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-100 rounded mr-2"></div>
                <span>Disponible</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-50 rounded mr-2"></div>
                <span>Indisponible</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 🎯 GÉNÉRATION DES CRÉNEAUX HORAIRES
  const getAvailableTimes = (): string[] => {
    if (!selectedDate) return [];
    
    const dayAvailability = calculateDayAvailability(selectedDate);
    return [
      ...dayAvailability.morning.availableSlots,
      ...dayAvailability.afternoon.availableSlots
    ];
  };

  const availableTimes = getAvailableTimes();

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 relative">
      <div className="mb-6">
        {isMultipleServices && multipleServices ? (
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Réservation groupée - {multipleServices.length} service{multipleServices.length > 1 ? 's' : ''}
            </h3>
            <div className="space-y-1 text-gray-600">
              {multipleServices.map((service, index) => (
                <p key={index} className="text-sm">
                  • {service.type === 'installation' ? 'Installation' : 'Réparation'} - {service.service} - {service.option}
                </p>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-semibold mb-2">
              {`${serviceType === 'installation' ? 'Installation' : 'Réparation'} - ${selectedService}`}
            </h3>
            <p className="text-gray-600">Option: {serviceOption}</p>
          </div>
        )}
        
        <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded-md">
          <p className="text-sm text-blue-800">
            Sélectionnez une date et un horaire pour votre rendez-vous
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Calendrier de sélection de date */}
          {renderCalendar()}

          {/* Sélection d'horaire */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4">
              <h3 className="text-lg font-semibold mb-3">Sélectionnez un horaire</h3>
              {selectedDate ? (
                <>
                  <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm font-medium text-gray-700">
                      Date sélectionnée: <span className="text-primary">{selectedDate.toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}</span>
                    </p>
                  </div>
                  
                  {availableTimes.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {availableTimes.map((time) => (
                        <button
                          key={time}
                          onClick={() => onTimeSelect(time)}
                          className={`py-3 px-2 text-center border rounded-md text-sm font-medium transition-colors
                            ${selectedTime === time 
                              ? "bg-primary text-white border-primary" 
                              : "hover:bg-gray-100 border-gray-300 text-gray-700"
                            }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-red-500 font-medium mb-2">
                        Aucun créneau disponible pour cette date
                      </p>
                      <p className="text-sm text-gray-600">
                        Veuillez sélectionner une autre date
                      </p>
                    </div>
                  )}
                  
                  {/* Informations sur les créneaux */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-md">
                    <p className="text-xs text-blue-700">
                      {serviceType === 'installation' 
                        ? "Les installations nécessitent plus de temps. Créneaux limités par jour."
                        : "Créneaux de réparation disponibles selon la charge de travail."
                      }
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    Veuillez d'abord sélectionner une date dans le calendrier ci-dessus
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
        <button
          onClick={onContinue}
          disabled={!selectedDate || !selectedTime}
          className="bg-primary text-white py-2 px-6 rounded-md hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continuer
        </button>
      </div>
    </div>
  );
}